// Criteria reducers for the Polaris slice: the criteria list, the criterion
// added from free text and the optimistic Essential flag. Split out of
// polarisSlice to keep that file within the repo's per-file size budget.
import {ActionReducerMapBuilder, isAnyOf} from "@reduxjs/toolkit";
import type {TPolarisReducerState} from "store/polaris/types";
import {addCriterionFromText, loadCriteria, setCriterionEssential} from "./criteriaThunks";

const criterionById = (state: TPolarisReducerState, criterionId: string) =>
	state.criteria?.criteria.find((c) => c.criterion_id === criterionId);

// Matchers only, so the slice can register them after all its addCase calls.
export const addCriteriaMatchers = (builder: ActionReducerMapBuilder<TPolarisReducerState>) => {
	builder
		.addMatcher(isAnyOf(loadCriteria.fulfilled), (state, action) => {
			state.criteria = action.payload;
		})
		// The thunk's re-read normally holds the new criterion already.
		.addMatcher(isAnyOf(addCriterionFromText.fulfilled), (state, action) => {
			const {criterion} = action.payload;
			const list = state.criteria?.criteria;

			if (!criterion || !list) return;
			if (!list.some((c) => c.criterion_id === criterion.criterion_id)) list.push(criterion);
		})
		// Essential: shown at once, before the PATCH answers.
		.addMatcher(isAnyOf(setCriterionEssential.pending), (state, action) => {
			const criterion = criterionById(state, action.meta.arg.criterionId);

			if (criterion) criterion.essential = action.meta.arg.essential;
		})
		// The toggle sends the opposite of what is on screen and ignores clicks
		// while its PATCH is in flight, so the value before is the opposite.
		.addMatcher(isAnyOf(setCriterionEssential.rejected), (state, action) => {
			const criterion = criterionById(state, action.meta.arg.criterionId);

			if (criterion) criterion.essential = !action.meta.arg.essential;
		})
		// The server's criterion wins; an answer without the flag (older
		// backend) keeps the value shown.
		.addMatcher(isAnyOf(setCriterionEssential.fulfilled), (state, action) => {
			const {criterionId} = action.meta.arg;
			const criterion = criterionById(state, criterionId);

			if (criterion && action.payload?.criterion_id === criterionId) {
				Object.assign(criterion, action.payload);
			}
		});
};
